import {
  Badge,
  Box,
  Button,
  Card,
  Heading,
  Spinner,
  Table,
  Text,
} from '@chakra-ui/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronDown } from 'lucide-react'
import React, { useCallback, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router'

import { ConfirmDialog, Modal } from '@mangovoice/mango-ui'
import type { ToastLike } from '@mangovoice/mango-ui'

import { DeleteBlockersList } from '../components/DeleteBlockersList'
import { EditPhoneNumberModal } from '../components/EditPhoneNumberModal'
import { getPhoneNumberTypeLabel } from '../constants'
import type { PhoneNumbersTranslateFn } from '../types/i18n.types'
import { text } from '../types/i18n.types'
import type {
  DeleteBlocker,
  PhoneNumberListItem,
  PhoneNumbersApi,
  PhoneNumberUpdatePayload,
} from '../types/phoneNumber.types'
import { ApiError } from '../utils/ApiError'
import type { BlockerRoutePaths } from '../utils/buildBlockerLink'
import { deriveCountryCode } from '../utils/deriveCountryCode'
import { formatApiFieldErrors } from '../utils/formatApiErrors'
import { formatPhoneNumber } from '../utils/formatPhoneNumber'
import { getProviderUrl } from '../utils/providerUrl'
import { getStatusColorPalette } from '../utils/statusColor'
import { getTypeColorPalette } from '../utils/typeColor'

// -- Key-value row helper --

const DetailRow: React.FC<{
  label: string
  children: React.ReactNode
}> = ({ label, children }) => (
  <Table.Row>
    <Table.Cell
      fontWeight="600"
      fontSize="sm"
      whiteSpace="nowrap"
      width="40%"
      py={2.5}
    >
      {label}
    </Table.Cell>
    <Table.Cell fontSize="sm" textAlign="right" py={2.5}>
      {children}
    </Table.Cell>
  </Table.Row>
)

// -- Section collapse persistence --

const SECTIONS_STORAGE_KEY = 'phoneNumbers.sections'

function getCollapsedSections(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(SECTIONS_STORAGE_KEY)
    if (raw) return JSON.parse(raw)
  } catch {
    // corrupted or unavailable
  }
  return {}
}

function isSectionOpen(sectionKey: string): boolean {
  return !getCollapsedSections()[sectionKey]
}

function setSectionOpen(sectionKey: string, open: boolean): void {
  try {
    const sections = getCollapsedSections()
    if (open) {
      delete sections[sectionKey]
    } else {
      sections[sectionKey] = true
    }
    if (Object.keys(sections).length === 0) {
      localStorage.removeItem(SECTIONS_STORAGE_KEY)
    } else {
      localStorage.setItem(SECTIONS_STORAGE_KEY, JSON.stringify(sections))
    }
  } catch {
    // localStorage unavailable
  }
}

// -- Section table wrapper --

const DetailSection: React.FC<{
  title: string
  sectionKey: string
  children: React.ReactNode
}> = ({ title, sectionKey, children }) => {
  const [open, setOpen] = useState(() => isSectionOpen(sectionKey))

  const toggle = useCallback(() => {
    setOpen((prev) => {
      const next = !prev
      setSectionOpen(sectionKey, next)
      return next
    })
  }, [sectionKey])

  return (
    <Card.Root variant="outline" mb={4} overflow="hidden">
      <Box
        bg="bg.subtle"
        px={4}
        py={2.5}
        borderBottomWidth={open ? '1px' : '0'}
        cursor="pointer"
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        onClick={toggle}
        userSelect="none"
      >
        <Text fontWeight="600" fontSize="sm">
          {title}
        </Text>
        <Box
          transition="transform 0.2s"
          transform={open ? 'rotate(0deg)' : 'rotate(-90deg)'}
        >
          <ChevronDown size={16} />
        </Box>
      </Box>
      {open && (
        <Table.Root size="sm" variant="line">
          <Table.Body>{children}</Table.Body>
        </Table.Root>
      )}
    </Card.Root>
  )
}

const ProviderCell: React.FC<{
  providerName: string | null | undefined
  remoteId: string | null | undefined
  phoneNumber: string | null | undefined
  t?: PhoneNumbersTranslateFn
  bandwidthAccountId?: string
  isMangoInternal?: boolean
}> = ({
  providerName,
  remoteId,
  phoneNumber,
  t,
  bandwidthAccountId,
  isMangoInternal,
}) => {
  const url = isMangoInternal
    ? getProviderUrl(providerName, remoteId, phoneNumber, bandwidthAccountId)
    : undefined
  return (
    <Box>
      <Text fontSize="sm">{providerName || '—'}</Text>
      {url && (
        <a href={url} target="_blank" rel="noopener noreferrer">
          <Text fontSize="xs" color="blue.fg">
            {text(
              t,
              'phoneNumbers.detail.viewInProvider',
              `View in ${providerName}`,
              { provider: providerName },
            )}
          </Text>
        </a>
      )}
    </Box>
  )
}

function formatDate(iso?: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

// -- Main component --

export interface PhoneNumberDetailPageProps {
  api: PhoneNumbersApi
  phoneNumberId: number
  listPath?: string
  pbxPath?: string
  t?: PhoneNumbersTranslateFn
  toast?: ToastLike
  isStaff?: boolean
  isSuperuser?: boolean
  bandwidthAccountId?: string
  /** Whether the current user can edit phone numbers */
  canEdit?: boolean
  /** Whether the current user can delete phone numbers */
  canDelete?: boolean
  /**
   * Route path templates used to deep-link blockers returned by a failed
   * delete (e.g. lines, switchboards, system settings). When omitted,
   * blocker names are rendered as plain text.
   */
  blockerRoutePaths?: BlockerRoutePaths
  /**
   * Deployment region (e.g. 'US' | 'GB'). Governs whether the Edit modal's
   * E911 section requires a state, mirroring the backend's
   * get_default_region().
   */
  defaultCountryCode?: string
}

function extractDeleteBlockers(error: unknown): DeleteBlocker[] | null {
  if (!(error instanceof ApiError)) return null
  const data = error.responseData
  if (!data || typeof data !== 'object') return null
  const blockers = (data as { blockers?: unknown }).blockers
  if (!Array.isArray(blockers) || blockers.length === 0) return null
  return blockers as DeleteBlocker[]
}

function extractDeleteDetail(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null
  const data = error.responseData
  if (!data || typeof data !== 'object') return null
  const { detail, message } = data as { detail?: unknown; message?: unknown }
  if (typeof detail === 'string' && detail.length > 0) return detail
  // Fall back to the legacy `ApiErrorFormatter` shape (`{type, code, message,
  // parameters}`) so CS still sees the human-readable reason string when the
  // backend hasn't shipped the structured `blockers` payload yet.
  if (typeof message === 'string' && message.length > 0) return message
  return null
}

export const PhoneNumberDetailPage: React.FC<PhoneNumberDetailPageProps> = ({
  api,
  phoneNumberId,
  listPath,
  pbxPath,
  t,
  toast,
  isStaff,
  isSuperuser,
  bandwidthAccountId,
  canEdit,
  canDelete,
  blockerRoutePaths,
  defaultCountryCode,
}) => {
  const isMangoInternal = isStaff || isSuperuser
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [isEditOpen, setIsEditOpen] = useState(false)
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [deleteBlockers, setDeleteBlockers] = useState<DeleteBlocker[] | null>(
    null,
  )
  const [deleteBlockedDetail, setDeleteBlockedDetail] = useState<string | null>(
    null,
  )

  const { data, isLoading, error } = useQuery({
    queryKey: ['phoneNumber', phoneNumberId],
    queryFn: async () => {
      const response = await api.getPhoneNumber(phoneNumberId, {
        includeDiagnostics: true,
      })
      if (!response.ok || !response.data) {
        throw new Error('Failed to load phone number')
      }
      return response.data
    },
  })

  const updateMutation = useMutation({
    mutationFn: async (payload: PhoneNumberUpdatePayload) => {
      const response = await api.updatePhoneNumber(phoneNumberId, payload)
      if (!response.ok) {
        throw new ApiError('Failed to update phone number', response.errors)
      }
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['phoneNumber', phoneNumberId],
      })
      queryClient.invalidateQueries({ queryKey: ['phoneNumbers'] })
      setIsEditOpen(false)
      toast?.create({
        title: text(
          t,
          'phoneNumbers.detail.toast.updateSuccess',
          'Phone number updated',
        ),
        type: 'success',
      })
    },
    onError: (error: Error) => {
      toast?.create({
        title: text(
          t,
          'phoneNumbers.detail.toast.updateFailed',
          'Failed to update phone number',
        ),
        description:
          error instanceof ApiError
            ? formatApiFieldErrors(error.fieldErrors)
            : undefined,
        type: 'error',
      })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const response = await api.deletePhoneNumber(phoneNumberId)
      if (!response.ok) {
        throw new ApiError(
          'Failed to delete phone number',
          response.errors,
          response.responseData,
        )
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['phoneNumbers'] })
      setIsDeleteOpen(false)
      setDeleteBlockers(null)
      setDeleteBlockedDetail(null)
      toast?.create({
        title: text(
          t,
          'phoneNumbers.detail.toast.deleteSuccess',
          'Phone number deleted',
        ),
        type: 'success',
      })
      if (listPath) {
        navigate(listPath)
      }
    },
    onError: (error: Error) => {
      const blockers = extractDeleteBlockers(error)
      if (blockers) {
        setDeleteBlockers(blockers)
        setDeleteBlockedDetail(extractDeleteDetail(error))
        return
      }

      // Prefer the server's human-readable message (structured `detail` from
      // the new API, or legacy `ApiErrorFormatter.message` during the window
      // where this PR is deployed but the API PR is not) before falling back
      // to field-error formatting.
      const description =
        extractDeleteDetail(error) ??
        (error instanceof ApiError
          ? formatApiFieldErrors(error.fieldErrors)
          : undefined)
      toast?.create({
        title: text(
          t,
          'phoneNumbers.detail.toast.deleteFailed',
          'Failed to delete phone number',
        ),
        description,
        type: 'error',
      })
    },
  })

  const closeDeleteDialog = useCallback(() => {
    setIsDeleteOpen(false)
    setDeleteBlockers(null)
    setDeleteBlockedDetail(null)
  }, [])

  // `deleteMutation.mutate` is a stable reference from TanStack Query, so we
  // depend on it directly instead of the whole `deleteMutation` object (which
  // is a new instance every render and would invalidate the memo).
  const handleDeleteConfirm = useCallback(() => {
    setDeleteBlockers(null)
    setDeleteBlockedDetail(null)
    deleteMutation.mutate()
  }, [deleteMutation.mutate])

  const restoreMutation = useMutation({
    mutationFn: async () => {
      const response = await api.restorePhoneNumber(phoneNumberId)
      if (!response.ok) {
        throw new Error('Failed to restore phone number')
      }
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['phoneNumber', phoneNumberId],
      })
      queryClient.invalidateQueries({ queryKey: ['phoneNumbers'] })
      toast?.create({
        title: text(
          t,
          'phoneNumbers.detail.toast.restoreSuccess',
          'Phone number restored',
        ),
        type: 'success',
      })
    },
    onError: () => {
      toast?.create({
        title: text(
          t,
          'phoneNumbers.detail.toast.restoreFailed',
          'Failed to restore phone number',
        ),
        type: 'error',
      })
    },
  })

  const noopSaveMutation = useMutation({
    mutationFn: async () => {
      if (!api.noopSave) throw new Error('Not supported')
      const response = await api.noopSave(phoneNumberId)
      if (!response.ok) throw new Error('Failed to noop-save')
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['phoneNumber', phoneNumberId],
      })
      toast?.create({
        title: text(
          t,
          'phoneNumbers.detail.toast.noopSaveSuccess',
          'Re-triggered side effects',
        ),
        type: 'success',
      })
    },
    onError: () => {
      toast?.create({
        title: text(
          t,
          'phoneNumbers.detail.toast.noopSaveFailed',
          'Noop-save failed',
        ),
        type: 'error',
      })
    },
  })

  const pbxId = data?.pbx_id

  const { data: statusOptions = [] } = useQuery({
    queryKey: ['phoneNumberStatuses'],
    queryFn: async () => {
      if (!api.getStatuses) return []
      const res = await api.getStatuses()
      return res.ok && res.data ? res.data : []
    },
    staleTime: 5 * 60 * 1000,
    enabled: isEditOpen,
  })

  const { data: providerOptions = [] } = useQuery({
    queryKey: ['phoneNumberProviders'],
    queryFn: async () => {
      if (!api.getProviders) return []
      const res = await api.getProviders()
      return res.ok && res.data ? res.data : []
    },
    staleTime: 5 * 60 * 1000,
    enabled: isEditOpen,
  })

  const { data: extensionOptions = [] } = useQuery({
    queryKey: ['phoneNumberExtensions', pbxId],
    queryFn: async () => {
      if (!api.getExtensions || !pbxId) return []
      const res = await api.getExtensions(pbxId)
      return res.ok && res.data ? res.data : []
    },
    staleTime: 5 * 60 * 1000,
    enabled: isEditOpen && !!pbxId,
  })

  const { data: faxMachineOptions = [] } = useQuery({
    queryKey: ['phoneNumberFaxMachines', pbxId],
    queryFn: async () => {
      if (!api.getFaxMachines || !pbxId) return []
      const res = await api.getFaxMachines(pbxId)
      return res.ok && res.data ? res.data : []
    },
    staleTime: 5 * 60 * 1000,
    enabled: isEditOpen && !!pbxId,
  })

  // Also load integrations when we'll render the SMS Relay Integration row —
  // without this the name lookup would miss until the edit modal is opened.
  // Scoped to mango-internal users since only they see the SMS Relay row /
  // integration field, and only when SMS Relay is on (the row is hidden
  // otherwise).
  const shouldLoadIntegrations =
    !!pbxId &&
    (isEditOpen || (isMangoInternal && !!data?.sms_information?.relay))
  const { data: integrationOptions = [] } = useQuery({
    queryKey: ['phoneNumberIntegrations', pbxId],
    queryFn: async () => {
      if (!api.getIntegrations || !pbxId) return []
      const res = await api.getIntegrations(pbxId)
      return res.ok && res.data ? res.data : []
    },
    staleTime: 5 * 60 * 1000,
    enabled: shouldLoadIntegrations,
  })

  const integrationNameById = useMemo(() => {
    const map = new Map<number, string>()
    for (const option of integrationOptions) {
      map.set(option.id, option.name)
    }
    return map
  }, [integrationOptions])

  const handleSave = useCallback(
    async (payload: PhoneNumberUpdatePayload) => {
      await updateMutation.mutateAsync(payload)
    },
    [updateMutation],
  )

  if (isLoading) {
    return (
      <Box textAlign="center" py={12}>
        <Spinner size="lg" />
        <Text mt={4} color="fg.muted">
          {text(t, 'phoneNumbers.detail.loading', 'Loading phone number...')}
        </Text>
      </Box>
    )
  }

  if (error || !data) {
    return (
      <Box textAlign="center" py={12}>
        <Text color="fg.error">
          {text(t, 'phoneNumbers.detail.error', 'Failed to load phone number')}
        </Text>
      </Box>
    )
  }

  const pn: PhoneNumberListItem = data
  const smsRelay = pn.sms_information?.relay ?? false
  const e911Registered = !!pn.e911_registered_at
  const isDeleted = !!pn.deleted_at

  return (
    <>
      {/* Header */}
      <Box mb={6}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box
            display="flex"
            alignItems="center"
            justifyContent={'center'}
            gap={3}
          >
            {listPath && (
              <Link to={listPath} style={{ textDecoration: 'none' }}>
                <Text
                  fontSize="xs"
                  color="fg.muted"
                  _hover={{ textDecoration: 'underline' }}
                >
                  ← {text(t, 'phoneNumbers.detail.backToList', 'Back to list')}
                </Text>
              </Link>
            )}
            <Heading size="xl" fontFamily="mono" mb={0}>
              {formatPhoneNumber(pn.number)}
            </Heading>
            {pn.status && (
              <Badge
                size="md"
                variant="subtle"
                colorPalette={getStatusColorPalette(pn.status)}
              >
                {pn.status.display}
              </Badge>
            )}
            {isDeleted && (
              <Badge size="md" colorPalette="error">
                {text(t, 'phoneNumbers.detail.deleted', 'Deleted')}
              </Badge>
            )}
          </Box>
          <Box display="flex" gap={2}>
            {isDeleted ? (
              canDelete !== false && (
                <Button
                  size="sm"
                  colorPalette="success"
                  borderRadius="lg"
                  onClick={() => restoreMutation.mutate()}
                  loading={restoreMutation.isPending}
                >
                  {text(t, 'phoneNumbers.detail.restoreButton', 'Restore')}
                </Button>
              )
            ) : (
              <>
                {canEdit !== false && (
                  <Button
                    size="sm"
                    borderRadius="lg"
                    onClick={() => setIsEditOpen(true)}
                  >
                    {text(
                      t,
                      'phoneNumbers.detail.editButton',
                      'Edit Phone Number',
                    )}
                  </Button>
                )}
                {canDelete !== false && (
                  <Button
                    size="sm"
                    colorPalette="error"
                    variant="surface"
                    borderRadius="lg"
                    onClick={() => setIsDeleteOpen(true)}
                  >
                    {text(t, 'phoneNumbers.detail.deleteButton', 'Delete')}
                  </Button>
                )}
                {isSuperuser && api.noopSave && (
                  <Button
                    size="sm"
                    variant="outline"
                    borderRadius="lg"
                    onClick={() => noopSaveMutation.mutate()}
                    loading={noopSaveMutation.isPending}
                  >
                    {text(t, 'phoneNumbers.detail.noopSaveButton', 'Re-save')}
                  </Button>
                )}
              </>
            )}
          </Box>
        </Box>
      </Box>

      {/* Staff-only warnings */}
      {isStaff && pn.warnings && pn.warnings.length > 0 && (
        <Box mb={4} display="flex" flexDirection="column" gap={2}>
          {pn.warnings.map((warning, idx) => (
            <Box
              key={idx}
              bg="bg.warning"
              borderWidth="1px"
              borderColor="warning.muted"
              borderRadius="md"
              px={4}
              py={2.5}
            >
              <Text fontSize="sm" fontWeight="600" color="orange.fg">
                {warning.title}
              </Text>
              <Text fontSize="xs" color="orange.fg">
                {warning.detail}
              </Text>
            </Box>
          ))}
        </Box>
      )}

      {/* Two-column layout */}
      <Box
        display="flex"
        flexDirection={{ base: 'column', xs: 'row' } as Record<string, string>}
        gap={4}
        alignItems="flex-start"
      >
        {/* Left column */}
        <Box flex={1} minW={0}>
          <DetailSection
            sectionKey="general"
            title={text(
              t,
              'phoneNumbers.detail.sectionGeneral',
              'General Information',
            )}
          >
            <DetailRow label={text(t, 'phoneNumbers.detail.fieldName', 'Name')}>
              {pn.name || '—'}
            </DetailRow>
            <DetailRow
              label={text(t, 'phoneNumbers.detail.fieldCallerId', 'Caller ID')}
            >
              {pn.caller_identification || '—'}
            </DetailRow>
            <DetailRow label={text(t, 'phoneNumbers.detail.fieldType', 'Type')}>
              {pn.type ? (
                <Badge
                  size="sm"
                  variant="subtle"
                  colorPalette={getTypeColorPalette(pn.type)}
                >
                  {getPhoneNumberTypeLabel(pn.type, t)}
                </Badge>
              ) : (
                '—'
              )}
            </DetailRow>
            <DetailRow
              label={text(t, 'phoneNumbers.detail.fieldStatus', 'Status')}
            >
              {pn.status ? (
                <Badge
                  size="sm"
                  variant="subtle"
                  colorPalette={getStatusColorPalette(pn.status)}
                >
                  {pn.status.display}
                </Badge>
              ) : (
                '—'
              )}
            </DetailRow>
            {isMangoInternal && (
              <DetailRow
                label={text(
                  t,
                  'phoneNumbers.detail.fieldCountryCode',
                  'Country Code',
                )}
              >
                {deriveCountryCode(pn.country_code, pn.number) || '—'}
              </DetailRow>
            )}
            {isMangoInternal && (
              <DetailRow
                label={text(
                  t,
                  'phoneNumbers.detail.fieldTollFree',
                  'Toll Free',
                )}
              >
                {pn.toll_free
                  ? text(t, 'phoneNumbers.list.yes', 'Yes')
                  : text(t, 'phoneNumbers.list.no', 'No')}
              </DetailRow>
            )}
          </DetailSection>

          <DetailSection
            sectionKey="routing"
            title={text(t, 'phoneNumbers.detail.sectionRouting', 'Routing')}
          >
            {isMangoInternal && pn.pbx && (
              <DetailRow label={text(t, 'phoneNumbers.detail.fieldPbx', 'PBX')}>
                <Box>
                  {pbxPath ? (
                    <Link to={pbxPath} style={{ textDecoration: 'none' }}>
                      <Text fontSize="sm" color="blue.fg">
                        {pn.pbx.name}
                      </Text>
                    </Link>
                  ) : (
                    <Text fontSize="sm">{pn.pbx.name}</Text>
                  )}
                  {!pn.pbx.active && (
                    <Badge size="sm" colorPalette="warning">
                      {text(t, 'phoneNumbers.detail.inactive', 'Inactive')}
                    </Badge>
                  )}
                </Box>
              </DetailRow>
            )}
            {isMangoInternal && (
              <DetailRow
                label={text(
                  t,
                  'phoneNumbers.detail.fieldPbxServerGroup',
                  'PBX Server Group',
                )}
              >
                {pn.pbx?.server_group
                  ? pn.pbx.server_group.name || pn.pbx.server_group.host
                  : '—'}
              </DetailRow>
            )}
            <DetailRow
              label={text(t, 'phoneNumbers.detail.fieldExtension', 'Extension')}
            >
              {pn.extension?.number ?? pn.call_endpoint ?? 'None'}
            </DetailRow>
            <DetailRow
              label={text(
                t,
                'phoneNumbers.detail.fieldSendFaxesFrom',
                'Send Faxes From',
              )}
            >
              {pn.fax_extension?.name ||
                text(t, 'phoneNumbers.detail.usePbxDefault', 'Use PBX Default')}
            </DetailRow>
            <DetailRow
              label={text(
                t,
                'phoneNumbers.detail.fieldSourceTracking',
                'Source Tracking',
              )}
            >
              {pn.prepend_string || '—'}
            </DetailRow>
            <DetailRow
              label={text(
                t,
                'phoneNumbers.detail.fieldLocalPresence',
                'Local Presence',
              )}
            >
              {pn.local_presence
                ? `Group ${pn.local_presence}`
                : text(t, 'phoneNumbers.detail.off', 'Off')}
            </DetailRow>
          </DetailSection>

          {isMangoInternal && (
            <DetailSection
              sectionKey="providers"
              title={text(
                t,
                'phoneNumbers.detail.sectionProviders',
                'Providers',
              )}
            >
              <DetailRow
                label={text(
                  t,
                  'phoneNumbers.detail.fieldVoiceProvider',
                  'Voice Provider',
                )}
              >
                <ProviderCell
                  providerName={pn.voice_provider}
                  remoteId={pn.voice_provider_remote_id}
                  phoneNumber={pn.number}
                  t={t}
                  bandwidthAccountId={bandwidthAccountId}
                  isMangoInternal={isMangoInternal}
                />
              </DetailRow>
              <DetailRow
                label={text(
                  t,
                  'phoneNumbers.detail.fieldSmsProvider',
                  'SMS Provider',
                )}
              >
                <ProviderCell
                  providerName={pn.sms_provider}
                  remoteId={pn.sms_provider_remote_id}
                  phoneNumber={pn.number}
                  t={t}
                  bandwidthAccountId={bandwidthAccountId}
                  isMangoInternal={isMangoInternal}
                />
              </DetailRow>
            </DetailSection>
          )}
        </Box>

        {/* Right column */}
        <Box flex={1} minW={0}>
          <DetailSection
            sectionKey="sms"
            title={text(t, 'phoneNumbers.detail.sectionSms', 'SMS')}
          >
            <DetailRow
              label={text(
                t,
                'phoneNumbers.detail.fieldSmsEnabled',
                'SMS Enabled',
              )}
            >
              {pn.sms_activated_at
                ? formatDate(pn.sms_activated_at)
                : text(t, 'phoneNumbers.list.no', 'No')}
            </DetailRow>
            {isMangoInternal && (
              <DetailRow
                label={text(
                  t,
                  'phoneNumbers.detail.fieldSmsRelay',
                  'SMS Relay',
                )}
              >
                {smsRelay
                  ? text(t, 'phoneNumbers.detail.on', 'On')
                  : text(t, 'phoneNumbers.detail.off', 'Off')}
              </DetailRow>
            )}
            {isMangoInternal && smsRelay && (
              <DetailRow
                label={text(
                  t,
                  'phoneNumbers.detail.fieldSmsRelayIntegration',
                  'SMS Relay Integration',
                )}
              >
                {pn.integration_id != null
                  ? (integrationNameById.get(pn.integration_id) ??
                    `#${pn.integration_id}`)
                  : '—'}
              </DetailRow>
            )}
            <DetailRow
              label={text(t, 'phoneNumbers.detail.fieldSmsGroup', 'SMS Group')}
            >
              {pn.sms_information?.group_name ?? '—'}
            </DetailRow>
            {isMangoInternal && (
              <DetailRow
                label={text(
                  t,
                  'phoneNumbers.detail.fieldCampaignId',
                  'Campaign ID',
                )}
              >
                {pn.campaign_id ? (
                  <>
                    {pn.campaign_id}
                    {pn.is_campaign_registered && (
                      <Text as="span" color="green.fg" ml={2} fontSize="xs">
                        (
                        {text(
                          t,
                          'phoneNumbers.detail.registered',
                          'Registered',
                        )}
                        )
                      </Text>
                    )}
                  </>
                ) : (
                  '—'
                )}
              </DetailRow>
            )}
          </DetailSection>

          {isStaff &&
            pn.telnyx_sms_diagnostics &&
            pn.telnyx_sms_diagnostics.length > 0 && (
              <DetailSection
                sectionKey="smsDiagnostics"
                title={text(
                  t,
                  'phoneNumbers.detail.sectionSmsDiagnostics',
                  'Telnyx SMS Diagnostics',
                )}
              >
                {pn.telnyx_sms_diagnostics.map((diag, idx) => (
                  <Table.Row key={idx}>
                    <Table.Cell fontSize="sm" py={2.5}>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Badge
                          size="sm"
                          colorPalette="warning"
                          variant="subtle"
                        >
                          {text(
                            t,
                            'phoneNumbers.detail.diagnosticWarning',
                            'Warning',
                          )}
                        </Badge>
                        <Text fontSize="sm">{diag}</Text>
                      </Box>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </DetailSection>
            )}

          <DetailSection
            sectionKey="e911"
            title={text(
              t,
              'phoneNumbers.detail.sectionE911',
              'E911 Information',
            )}
          >
            <DetailRow
              label={text(
                t,
                'phoneNumbers.detail.fieldE911Status',
                'Registered',
              )}
            >
              {e911Registered
                ? text(t, 'phoneNumbers.list.e911Verified', 'Verified')
                : text(
                    t,
                    'phoneNumbers.detail.notRegistered',
                    'Not Registered',
                  )}
            </DetailRow>
            <DetailRow
              label={text(t, 'phoneNumbers.detail.fieldE911Address', 'Address')}
            >
              {pn.e911_information?.address || '—'}
            </DetailRow>
            <DetailRow
              label={text(
                t,
                'phoneNumbers.detail.fieldE911Address2',
                'Address 2',
              )}
            >
              {pn.e911_information?.address_2 || '—'}
            </DetailRow>
            <DetailRow
              label={text(t, 'phoneNumbers.detail.fieldE911City', 'City')}
            >
              {pn.e911_information?.city || '—'}
            </DetailRow>
            <DetailRow
              label={text(t, 'phoneNumbers.detail.fieldE911State', 'State')}
            >
              {pn.e911_information?.state || '—'}
            </DetailRow>
            <DetailRow
              label={text(
                t,
                'phoneNumbers.detail.fieldE911PostalCode',
                'Postal Code',
              )}
            >
              {pn.e911_information?.postal_code || '—'}
            </DetailRow>
          </DetailSection>

          {isMangoInternal && pn.hepic_url && (
            <DetailSection
              sectionKey="metrics"
              title={text(t, 'phoneNumbers.detail.sectionMetrics', 'Metrics')}
            >
              <DetailRow
                label={text(
                  t,
                  'phoneNumbers.detail.fieldCallStats',
                  'Call Stats',
                )}
              >
                <a
                  href={pn.hepic_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Text fontSize="sm" color="blue.fg">
                    {text(t, 'phoneNumbers.detail.callStatsLink', 'Call Stats')}
                  </Text>
                </a>
              </DetailRow>
            </DetailSection>
          )}

          {isMangoInternal && (
            <DetailSection
              sectionKey="timestamps"
              title={text(
                t,
                'phoneNumbers.detail.sectionTimestamps',
                'Timestamps',
              )}
            >
              <DetailRow
                label={text(t, 'phoneNumbers.detail.fieldCreatedAt', 'Created')}
              >
                {formatDate(pn.created_at)}
              </DetailRow>
              <DetailRow
                label={text(t, 'phoneNumbers.detail.fieldUpdatedAt', 'Updated')}
              >
                {formatDate(pn.updated_at)}
              </DetailRow>
            </DetailSection>
          )}
        </Box>
      </Box>

      <EditPhoneNumberModal
        phoneNumber={pn}
        open={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        onSave={handleSave}
        isSaving={updateMutation.isPending}
        t={t}
        statuses={statusOptions}
        providers={providerOptions}
        extensions={extensionOptions}
        faxMachines={faxMachineOptions}
        integrations={integrationOptions}
        isMangoInternal={isMangoInternal}
        defaultCountryCode={defaultCountryCode}
      />

      {deleteBlockers ? (
        <Modal
          open={isDeleteOpen}
          onOpenChange={(details) => !details.open && closeDeleteDialog()}
          role="alertdialog"
          title={text(
            t,
            'phoneNumbers.detail.deleteBlockedTitle',
            "Can't delete phone number",
          )}
          maxWidth="480px"
          footer={
            <Box display="flex" gap={3} justifyContent="flex-end">
              <Button
                type="button"
                variant="outline"
                colorPalette="neutral"
                onClick={closeDeleteDialog}
                px={5}
                py={2.5}
                borderRadius="lg"
                borderColor="border.default"
              >
                {text(t, 'phoneNumbers.detail.deleteBlockedClose', 'Close')}
              </Button>
            </Box>
          }
        >
          <Box display="flex" flexDirection="column" gap={3}>
            <Text fontSize="sm">
              {deleteBlockedDetail ??
                text(
                  t,
                  'phoneNumbers.detail.deleteBlockedDescription',
                  "This phone number can't be deleted because it's still in use.",
                )}
            </Text>
            <DeleteBlockersList
              blockers={deleteBlockers}
              routePaths={blockerRoutePaths ?? {}}
            />
          </Box>
        </Modal>
      ) : (
        <ConfirmDialog
          open={isDeleteOpen}
          onClose={closeDeleteDialog}
          onConfirm={handleDeleteConfirm}
          isLoading={deleteMutation.isPending}
          variant="destructive"
          title={text(
            t,
            'phoneNumbers.detail.deleteTitle',
            'Delete Phone Number',
          )}
          description={text(
            t,
            'phoneNumbers.detail.deleteConfirmation',
            'This will soft-delete the phone number. It can be restored later if needed. Are you sure?',
          )}
          confirmLabel={text(t, 'phoneNumbers.detail.deleteButton', 'Delete')}
          cancelLabel={text(t, 'phoneNumbers.edit.cancel', 'Cancel')}
        />
      )}
    </>
  )
}
